import courseImage from '../assets/map/training-course/course-painted-v2.webp';
import mountainImage from '../assets/map/training-course/mountain-painted.webp';
import runImage from '../assets/map/training-course/run.webp';
import swimImage from '../assets/map/training-course/swim.webp';
import walkImage from '../assets/map/training-course/walk.webp';
import climbImage from '../assets/map/training-course/climb.webp';

// Scenery, supporting characters and wildlife are painted into these backgrounds.
// Only the current trainee is rendered separately and follows the paths below.
export const COURSE_CANVAS = { width: 724, height: 1510, image: courseImage };
export const MOUNTAIN_CANVAS = { width: 900, height: 1200, image: mountainImage };

// Artwork coordinates, not screen pixels. Feet (or the swimmer's waterline) sit on the path.
// Scale describes perspective at each point; it is independent of learning pace.
const points = (values) => values.map(([x, y, scale]) => ({ x, y, scale }));
export const TRAINING_STAGES = [
  { id: 'meadow', endLevel: 20, top: 0, image: runImage, height: 108, anchor: 1,
    path: points([[367, 150, .7], [414, 178, .75], [392, 202, .8], [331, 226, .84], [290, 254, .88], [293, 277, .91], [327, 307, .96], [364, 360, 1]]) },
  { id: 'river', endLevel: 40, top: 360, image: swimImage, height: 62, anchor: .85,
    path: points([[364, 360, .7], [364, 422, .78], [368, 484, .86], [376, 562, .94], [367, 660, 1]]) },
  { id: 'night', endLevel: 60, top: 660, image: walkImage, height: 108, anchor: 1,
    path: points([[367, 660, .7], [331, 696, .73], [326, 728, .76], [378, 764, .79], [434, 803, .82], [407, 844, .85], [324, 886, .88], [270, 925, .91], [296, 967, .94], [362, 1010, .97], [397, 1058, 1], [367, 1100, 1]]) },
  { id: 'obstacles', endLevel: 80, top: 1100, image: runImage, height: 108, anchor: 1,
    path: points([[367, 1100, .7], [290, 1148, .76], [282, 1196, .8], [365, 1230, .85], [446, 1269, .89], [416, 1320, .92], [316, 1380, .96], [316, 1418, .98], [402, 1450, 1], [502, 1500, 1]]) },
  { id: 'mountain', endLevel: 100, top: 0, image: climbImage, height: 130, anchor: 1,
    // Independent final scene: enter at its bottom and ascend toward its top.
    path: points([[445, 1145, 1], [433, 1064, .98], [388, 1010, .96], [425, 945, .94], [518, 886, .91], [590, 829, .89], [604, 781, .87], [553, 741, .85], [440, 700, .83], [357, 652, .81], [355, 617, .8], [430, 577, .78], [548, 539, .77], [628, 494, .76], [627, 460, .75], [526, 431, .74], [406, 402, .73], [334, 373, .72], [346, 343, .72], [466, 311, .71], [579, 285, .71], [609, 263, .7], [574, 239, .7], [491, 217, .7], [450, 198, .7], [468, 178, .7], [450, 153, .7]]) },
];

export function getTrainingLevel(summary = {}) {
  const rawExp = Number(summary.exp ?? summary.totalScoreGained ?? 0);
  const exp = Number.isFinite(rawExp) ? Math.max(0, Math.floor(rawExp)) : 0;
  const rawStep = Number(summary.levelExpStep);
  const step = Number.isFinite(rawStep) && rawStep >= 1 ? Math.floor(rawStep) : 5000;
  return Math.floor(exp / step) + 1;
}

export function getCoursePoint(path, ratio = 0) {
  const progress = Number.isFinite(ratio) ? Math.max(0, Math.min(1, ratio)) : 0;
  const distances = path.slice(1).map((end, i) => Math.hypot(end.x - path[i].x, end.y - path[i].y));
  let remaining = distances.reduce((sum, length) => sum + length, 0) * progress;
  for (let i = 0; i < distances.length; i += 1) {
    if (remaining <= distances[i] || i === distances.length - 1) {
      const fraction = distances[i] ? Math.min(1, remaining / distances[i]) : 0;
      const start = path[i];
      const end = path[i + 1];
      return {
        x: start.x + (end.x - start.x) * fraction,
        y: start.y + (end.y - start.y) * fraction,
        scale: start.scale + (end.scale - start.scale) * fraction,
      };
    }
    remaining -= distances[i];
  }
  return path[0];
}

export function getTrainingCourse(value = 1) {
  const level = Number.isFinite(Number(value)) ? Math.max(1, Math.floor(Number(value))) : 1;
  const cycle = Math.floor((level - 1) / 100) + 1;
  const offset = (cycle - 1) * 100;
  const localLevel = level - offset;
  const stageIndex = Math.floor((localLevel - 1) / 20);
  const stage = TRAINING_STAGES[stageIndex];
  const ratio = (localLevel - stageIndex * 20 - 1) / 19;
  return {
    level, cycle, offset, localLevel, stageIndex, stage, ratio,
    targetLevel: cycle * 100,
    completedCourses: Math.floor(level / 100),
    completedStages: TRAINING_STAGES.filter((item) => localLevel >= item.endLevel).map((item) => item.id),
    percent: ((stageIndex + ratio) / 5) * 100,
    point: getCoursePoint(stage.path, ratio),
  };
}
